// SvgConverter.jsx - Optimized Version
import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
  useMemo,
  memo,
} from "react";
import "./CSS/SvgConverter.css";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import ButtonGroup from "@mui/material/ButtonGroup";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import axios from "axios";
import { useUser } from "./Context/UserContext";

/* ---------------------------------------------------
   1) Context + RecursiveAccordion component
--------------------------------------------------- */
export const PathContext = React.createContext([]);

// Geliştirilmiş debounce helper
function debounce(fn, delay) {
  let t;
  const wrapped = (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
  wrapped.cancel = () => clearTimeout(t);
  wrapped.flush = () => {
    clearTimeout(t);
    fn();
  };
  return wrapped;
}

export function normalizeSlashSpacing(input = "") {
  // collapse multiple spaces
  let s = input.replace(/\s+/g, " ");
  // remove spaces around slashes
  s = s.replace(/\s*\/\s*/g, "/");
  // trim stray leading/trailing slashes/spaces
  s = s.replace(/^\/+|\/+$/g, "").trim();
  return s;
}

// encode each segment safely for URLs (images, GETs)
export function toUrlPath(input = "") {
  const norm = normalizeSlashSpacing(input);
  return norm.split("/").map(encodeURIComponent).join("/");
}

// RecursiveAccordion: Memoized
const RecursiveAccordion = React.memo(
  function RecursiveAccordion({ data, onPick }) {
    const [hoveredItem, setHoveredItem] = useState(null);
    const [expanded, setExpanded] = useState({});
    const currentPath = useContext(PathContext);

    const newPath = data?.name ? [...currentPath, data.name] : currentPath;
    const fullPath = newPath.join(".");
    useEffect(() => {
      if (data?.autoOpen) {
        setExpanded((prev) => ({ ...prev, [fullPath]: true }));
      }
    }, [fullPath, data?.autoOpen]);

    const handleChange = useCallback(
      (_, isExpanded) => {
        setExpanded((prev) => ({ ...prev, [fullPath]: isExpanded }));
      },
      [fullPath]
    );

    if (!data) return null;

    return (
      <PathContext.Provider value={newPath}>
        <Accordion
          expanded={!!expanded[fullPath]}
          onChange={handleChange}
          style={{
            background: data.background,
            borderRadius: data.borderRadius,
            overflow: "hidden",
            position: "relative",
          }}
          onMouseEnter={() => setHoveredItem(data.name)}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <AccordionSummary
            expandIcon={<ArrowDownwardIcon />}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography>{data.name}</Typography>
            {data.url && (
              <img
                src={
                  hoveredItem === data.name && data.hoverImageUrl
                    ? data.hoverImageUrl
                    : data.url
                }
                alt="icon"
                style={{ width: 50, height: 50 }}
              />
            )}
          </AccordionSummary>

          <AccordionDetails
            style={{
              maxHeight: data.maxHeight,
              overflow: "auto",
              background: data.background,
            }}
          >
            {Array.isArray(data.details) &&
              data.details.map((detail, idx) => {
                const type =
                  detail.name === "SubShop1Button"
                    ? "btn"
                    : detail.subShops
                    ? "grp"
                    : "leaf";
                const label =
                  detail.name || detail.buttonName || detail.url || "item";
                const itemKey = `${fullPath}::${type}::${label}::${idx}`;

                if (detail.name === "SubShop1Button") {
                  return (
                    <div key={itemKey}>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "10px",
                        }}
                      >
                        <Button
                          onClick={() => onPick(newPath, detail)}
                          variant="contained"
                        >
                          {detail.url &&
                            !String(detail.url).includes("EmptySvg") && (
                              <img
                                src={detail.url}
                                alt="icon"
                                style={{
                                  width: 50,
                                  height: 50,
                                  marginRight: 8,
                                  borderRadius: 4,
                                }}
                              />
                            )}
                          {detail.buttonName}
                        </Button>
                      </div>
                    </div>
                  );
                } else if (detail.subShops) {
                  return (
                    <RecursiveAccordion
                      key={itemKey}
                      data={{
                        name: detail.name,
                        details: detail.subShops,
                        maxHeight: detail.maxHeight,
                        autoOpen: detail.autoOpen,
                        buttonText: detail.buttonText,
                        background: detail.background,
                        url: detail.url,
                        hoverImageUrl: detail.hoverImageUrl,
                      }}
                      onPick={onPick}
                    />
                  );
                } else {
                  return <div key={itemKey}>{detail.name}</div>;
                }
              })}
          </AccordionDetails>
        </Accordion>
      </PathContext.Provider>
    );
  },
  (prev, next) => prev.data === next.data
);

const SvgPreview = memo(({ svgContent, svgExample }) => {
  const displayContent = svgExample || svgContent;

  if (!displayContent) {
    return (
      <div>
        <p>Lütfen Düzenlemek İstediğiniz Dizaynı Seçiniz.</p>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "10px",
        boxSizing: "border-box",
      }}
    >
      <iframe
        srcDoc={`
          <html>
            <head>
              <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                html, body { height: 100%; overflow: hidden; }
                body { 
                  display: flex; 
                  justify-content: center; 
                  align-items: center; 
                  padding: 10px;
                }
                svg { 
                  max-width: 100%; 
                  max-height: 100%; 
                  width: auto; 
                  height: auto; 
                  display: block;
                }
              </style>
            </head>
            <body>${displayContent}</body>
          </html>
        `}
        style={{
          width: "100%",
          height: "100%",
          border: "1px solid #ccc",
          borderRadius: "4px",
        }}
        title="SVG Preview"
      />
    </div>
  );
});

const SvgConverter = () => {
  let status = "1";
  let path =
    status === "0" ? "http://localhost:3003" : "https://outlettee.com:3003";
  const { user } = useUser(); 
  // Text state'leri için useRef kullanarak kontrollü input delay'i azaltma
  const [textInputs, setTextInputs] = useState({
    text: "",
    text1: "",
    text2: "",
    text3: "",
    textArea: "",
  });

  // Diğer state'ler
  const [imageUrl, setImageUrl] = useState("");
  const [rectangleStates, setRectangleStates] = useState({
    rectangleX: "",
    rectangleY: "",
    rectangleWidth: "",
    rectangleHeight: "",
    rectangleX2: "",
    rectangleY2: "",
    rectangleWidth2: "",
    rectangleHeight2: "",
    rectangleX3: "",
    rectangleY3: "",
    rectangleWidth3: "",
    rectangleHeight3: "",
    rectangleX4: "",
    rectangleY4: "",
    rectangleWidth4: "",
    rectangleHeight4: "",
  });

  const [textHeights, setTextHeights] = useState({
    textHeight1: "",
    textHeight2: "",
    textHeight3: "",
    textHeight4: "",
  });

  const [fontFamilies, setFontFamilies] = useState({
    fontFamily: "",
    fontFamily2: "",
    fontFamily3: "",
    fontFamily4: "",
  });

  const [textCurves, setTextCurves] = useState({
    textCurve1: "",
    textCurve2: "",
    textCurve3: "",
    textCurve4: "",
  });

  const [rotationAngles, setRotationAngles] = useState({
    rotationAngle1: 0,
    rotationAngle2: 0,
    rotationAngle3: 0,
    rotationAngle4: 0,
  });

  const [visibility, setVisibility] = useState({
    text1Visible: false,
    text2Visible: false,
    text3Visible: false,
    text4Visible: false,
    textAreaVisible: false,
  });

  const [selectedOption, setSelectedOption] = useState("");
  const [selectedCheckbox, setSelectedCheckbox] = useState("");
  const [selectedAligment, setselectedAligment] = useState("left");
  const [fontStatus, setFontStatus] = useState("false");
  const [designType, setDesignType] = useState("");
  const [lineCounts, setLineCounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [fileName, setFileName] = useState("processed-image.svg");
  const [lineHeight, setLineHeight] = useState("55");
  const [hoverImgVisible, setHoverImgVisible] = useState(false);
  const [hoverImgUrl, setHoverImgUrl] = useState("");
  const [shopData, setShopData] = useState([]);
  const shopDataRef = useRef([]);
  const [presentPath, setPresentPath] = useState("");
  const [svgContent, setSvgContent] = useState("");
  const [svgExample, setSvgExample] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedSide, setSelectedSide] = useState("");
  const [dimensions, setDimensions] = useState({ width: "", height: "" });

  // Products data - memoized
  const products = useMemo(
    () => [
      {
      name: "Men Wallet",
      engraveSides: [
        { side: "Front", width: "3.5", height: "4.5" },
        { side: "Inside Right", width: "1.4", height: "3.55" },
        { side: "Inside Left", width: "1.4", height: "3.55" },
      ],
    },
       {
      name: "Slim Wallet",
      engraveSides: [{ side: "Front", width: "2.5", height: "3" }],
    },
    {
      name: "Card Holder",
      engraveSides: [
        { side: "Front", width: "2.75", height: "3.75" },
        { side: "Inside Right", width: "2.3", height: "2.3" },
        { side: "Inside Left", width: "2.3", height: "2.3" },
      ],
    },
    {
      name: "Makeup Bag",
      engraveSides: [{ side: "Front", width: "4", height: "9.5" }],
    },
    {
      name: "Jewelry Box",
      engraveSides: [{ side: "Front", width: "3.5", height: "3.5" }],
    },
    {
      name: "Velvet Dog Collar",
      engraveSides: [{ side: "Front", width: "0.6", height: "1" }],
    },
    {
      name: "Leather Dog Collar",
      engraveSides: [{ side: "Front", width: "0.4", height: "1.5" }],
    },
    {
      name: "Dopp Kit L",
      engraveSides: [
        { side: "Front", width: "3.75", height: "7.5" },
        { side: "Back", width: "3.75", height: "7.5" },
      ],
    },
    {
      name: "Dopp Kit XL",
      engraveSides: [
        { side: "Front", width: "4", height: "9" },
        { side: "Back", width: "4", height: "9" },
      ],
    },
    {
      name: "Bookmark",
      engraveSides: [{ side: "Front", width: "1.2", height: "6" }],
    },
    {
      name: "Tray Storage",
      engraveSides: [{ side: "Front", width: "6", height: "6" }],
    },
    {
      name: "Ring Box",
      engraveSides: [{ side: "Front", width: "2", height: "2" }],
    },
    {
      name: "Guitar Pick",
      engraveSides: [
        { side: "Case", width: "1.75", height: "1.75" },
        { side: "Pick", width: "0.75", height: "1" },
      ],
    },
    {
      name: "Cutting Board M",
      engraveSides: [{ side: "Front", width: "7", height: "10" }],
    },
    {
      name: "Cutting Board L",
      engraveSides: [{ side: "Front", width: "9", height: "13.75" }],
    },
    {
      name: "Knifes",
      engraveSides: [
        { side: "Handle", width: "6", height: "6" },
        { side: "Sharp", width: "4", height: "4" },
      ],
    },
    ],
    []
  );

  const CustomButton = styled(Button)({
    borderColor: "black",
    color: "black",
    "&:hover": {
      borderColor: "rgba(121, 147, 81)",
      backgroundColor: "rgba(122, 178, 178, 0.1)",
    },
  });

  // Fetch shop data
 useEffect(() => {
  let mounted = true;
  
  const fetchShopData = async () => {
    if (!user?.ID) {
      console.error("User ID not found");
      return;
    }

    try {
      const response = await axios.post(path + "/api/shopDataFiltered", {
        UserID: user.ID,
        Admin: user.Admin || 0,
        DesignAdmin: user.DesignAdmin || 0
      });
      console.log("Shop data fetched:", response.data);
      if (!mounted) return;

      setShopData(response.data);
      shopDataRef.current = response.data;
      
    } catch (error) {
      console.error("Error fetching shop data:", error);
      if (mounted) {
        setShopData([]);
        shopDataRef.current = [];
      }
    }
  };

  fetchShopData();

  return () => {
    mounted = false;
  };
}, [user])

  // SVG content fetcher
  useEffect(() => {
    setSvgExample("");
    if (!imageUrl) {
      setSvgContent("");
      return;
    }
    fetch(imageUrl)
      .then((response) => response.text())
      .then((data) => setSvgContent(data))
      .catch(() => setSvgContent(""));
  }, [imageUrl]);

  // Optimized input handler with local state updates
  const handleChange = useCallback((event) => {
    const { id, value } = event.target;

    // Text input'ları için batch update
    if (
      id === "textOne" ||
      id === "textTwo" ||
      id === "textThree" ||
      id === "textFour" ||
      id === "textArea"
    ) {
      const fieldMap = {
        textOne: "text",
        textTwo: "text1",
        textThree: "text2",
        textFour: "text3",
        textArea: "textArea",
      };

      setTextInputs((prev) => ({
        ...prev,
        [fieldMap[id]]: value,
      }));

      if (id === "textArea") {
        setFontStatus("true");
        const lines = value.split("\n");
        setLineCounts(lines.map((line) => line.length));
      } else {
        setFontStatus("false");
      }
    } else if (id === "outlined-number") {
      setLineHeight(value);
      setFontStatus("true");
    }
  }, []);

  const commitOnBlur = useCallback(
    (id) => (e) => {
      handleChange({ target: { id, value: e.target.value } });
    },
    [handleChange]
  );

  // Memoized rects calculation
  const rects = useMemo(
    () => [
      {
        x: rectangleStates.rectangleX,
        y: rectangleStates.rectangleY,
        width: rectangleStates.rectangleWidth,
        height: rectangleStates.rectangleHeight,
        text1: textInputs.text || textInputs.textArea,
        textHeight: textHeights.textHeight1,
        fontFamily: fontFamilies.fontFamily,
        textCurve: textCurves.textCurve1,
        rotationAngle: rotationAngles.rotationAngle1,
      },
      {
        x: rectangleStates.rectangleX2,
        y: rectangleStates.rectangleY2,
        width: rectangleStates.rectangleWidth2,
        height: rectangleStates.rectangleHeight2,
        text2: textInputs.text1,
        textHeight: textHeights.textHeight2,
        fontFamily: fontFamilies.fontFamily2 || fontFamilies.fontFamily,
        textCurve: textCurves.textCurve2,
        rotationAngle: rotationAngles.rotationAngle2,
      },
      {
        x: rectangleStates.rectangleX3,
        y: rectangleStates.rectangleY3,
        width: rectangleStates.rectangleWidth3,
        height: rectangleStates.rectangleHeight3,
        text3: textInputs.text2,
        textHeight: textHeights.textHeight3,
        fontFamily: fontFamilies.fontFamily3 || fontFamilies.fontFamily,
        textCurve: textCurves.textCurve3,
        rotationAngle: rotationAngles.rotationAngle3,
      },
      {
        x: Number(rectangleStates.rectangleX4),
        y: Number(rectangleStates.rectangleY4),
        width: Number(rectangleStates.rectangleWidth4),
        height: Number(rectangleStates.rectangleHeight4),
        text4: textInputs.text3,
        textHeight: Number(textHeights.textHeight4),
        fontFamily: fontFamilies.fontFamily4 || fontFamilies.fontFamily,
        textCurve: textCurves.textCurve4,
        rotationAngle: rotationAngles.rotationAngle4,
      },
    ],
    [
      rectangleStates,
      textInputs,
      textHeights,
      fontFamilies,
      textCurves,
      rotationAngles,
    ]
  );

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleOptionChange = (event) => setSelectedOption(event.target.value);
  const handleOptionChangeFont = (event) =>
    setFontFamilies((prev) => ({ ...prev, fontFamily: event.target.value }));

  const processSVG = useCallback(
    async (test) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(
        svgContent || "<svg />",
        "image/svg+xml"
      );
      const textElement = doc.createElementNS(
        "http://www.w3.org/2000/svg",
        "text"
      );
      textElement.textContent = textInputs.text;
      doc.documentElement.appendChild(textElement);
      const serializedSVG = new XMLSerializer().serializeToString(doc);

      return fetch(path + "/process-svg", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: textInputs.text,
          svg: serializedSVG,
          rects,
          fontFamily: fontFamilies.fontFamily,
          fontStatus,
          alignment: selectedAligment,
          designType,
          test,
          lineHeight,
          rotationAngle: rotationAngles.rotationAngle1,
          dimensions,
        }),
      });
    },
    [
      svgContent,
      textInputs.text,
      rects,
      fontFamilies.fontFamily,
      fontStatus,
      selectedAligment,
      designType,
      lineHeight,
      rotationAngles.rotationAngle1,
      dimensions,
      path,
    ]
  );

  const downloadSVG = async () => {
    try {
      const processedSVG = await processSVG(false);
      const blob = await processedSVG.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      handleClose();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // Daha uzun debounce süresi ile preview fonksiyonu
  const trySVGRaw = useCallback(async () => {
    try {
      setSvgExample("");
      const processedSVG = await processSVG(true);
      const data = await processedSVG.text();
      setSvgExample(data);
    } catch (error) {
      console.error("Error fetching SVG:", error);
    }
  }, [processSVG]);

  // Preview debounce süresini artır
  const trySVGDebounced = useMemo(
    () => debounce(trySVGRaw, 800), // 300ms'den 800ms'ye çıkarıldı
    [trySVGRaw]
  );

  // onPick callback optimized
  const onPick = useCallback((newPath, detail) => {
    // Batch state updates
    setTextInputs({
      text: "",
      text1: "",
      text2: "",
      text3: "",
      textArea: "",
    });

    setImageUrl(detail.url || "");

    setRectangleStates({
      rectangleX: detail.x1 || "",
      rectangleY: detail.y1 || "",
      rectangleWidth: detail.width1 || "",
      rectangleHeight: detail.height1 || "",
      rectangleX2: detail.x2 || "",
      rectangleY2: detail.y2 || "",
      rectangleWidth2: detail.width2 || "",
      rectangleHeight2: detail.height2 || "",
      rectangleX3: detail.x3 || "",
      rectangleY3: detail.y3 || "",
      rectangleWidth3: detail.width3 || "",
      rectangleHeight3: detail.height3 || "",
      rectangleX4: detail.x4 || "",
      rectangleY4: detail.y4 || "",
      rectangleWidth4: detail.width4 || "",
      rectangleHeight4: detail.height4 || "",
    });

    setTextHeights({
      textHeight1: detail.textHeight1 || "",
      textHeight2: detail.textHeight2 || "",
      textHeight3: detail.textHeight3 || "",
      textHeight4: detail.textHeight4 || "",
    });

    setFontFamilies({
      fontFamily: detail.fontFamily || "",
      fontFamily2: detail.fontFamily2 || "",
      fontFamily3: detail.fontFamily3 || "",
      fontFamily4: detail.fontFamily4 || "",
    });

    setVisibility({
      text1Visible: !!detail.text1Visible,
      text2Visible: !!detail.text2Visible,
      text3Visible: !!detail.text3Visible,
      text4Visible: !!detail.text4Visible,
      textAreaVisible: !!detail.textAreaVisible,
    });

    setTextCurves({
      textCurve1: detail.textCurve1 || "",
      textCurve2: detail.textCurve2 || "",
      textCurve3: detail.textCurve3 || "",
      textCurve4: detail.textCurve4 || "",
    });

    setRotationAngles({
      rotationAngle1: detail.rotationAngle1 || 0,
      rotationAngle2: detail.rotationAngle2 || 0,
      rotationAngle3: detail.rotationAngle3 || 0,
      rotationAngle4: detail.rotationAngle4 || 0,
    });

    setDesignType(detail.designType || "");
    setPresentPath(newPath.join(" > "));
  }, []);

  const handleCheckboxChange = (event) =>
    setSelectedCheckbox(event.target.name);
  const handleAlignmentChange = (newAlignment) =>
    setselectedAligment(newAlignment);

  // Arrow button handler optimized
  const handleButtonClick = useCallback(
    (event) => {
      const buttonId = event.currentTarget.id;
      const step = 5;

      if (!svgContent) {
        console.log("SVG henüz yüklenmedi");
        return;
      }

      setRectangleStates((prev) => {
        const newState = { ...prev };

        if (selectedCheckbox === "textOne") {
          if (buttonId === "up")
            newState.rectangleY = Number(prev.rectangleY) - step;
          else if (buttonId === "left")
            newState.rectangleX = Number(prev.rectangleX) - step;
          else if (buttonId === "right")
            newState.rectangleX = Number(prev.rectangleX) + step;
          else if (buttonId === "down")
            newState.rectangleY = Number(prev.rectangleY) + step;
        } else if (selectedCheckbox === "textTwo") {
          if (buttonId === "up")
            newState.rectangleY2 = Number(prev.rectangleY2) - step;
          else if (buttonId === "left")
            newState.rectangleX2 = Number(prev.rectangleX2) - step;
          else if (buttonId === "right")
            newState.rectangleX2 = Number(prev.rectangleX2) + step;
          else if (buttonId === "down")
            newState.rectangleY2 = Number(prev.rectangleY2) + step;
        } else if (selectedCheckbox === "textThree") {
          if (buttonId === "up")
            newState.rectangleY3 = Number(prev.rectangleY3) - step;
          else if (buttonId === "left")
            newState.rectangleX3 = Number(prev.rectangleX3) - step;
          else if (buttonId === "right")
            newState.rectangleX3 = Number(prev.rectangleX3) + step;
          else if (buttonId === "down")
            newState.rectangleY3 = Number(prev.rectangleY3) + step;
        } else if (selectedCheckbox === "textFour") {
          if (buttonId === "up")
            newState.rectangleY4 = Number(prev.rectangleY4) - step;
          else if (buttonId === "left")
            newState.rectangleX4 = Number(prev.rectangleX4) - step;
          else if (buttonId === "right")
            newState.rectangleX4 = Number(prev.rectangleX4) + step;
          else if (buttonId === "down")
            newState.rectangleY4 = Number(prev.rectangleY4) + step;
        }

        return newState;
      });

      trySVGDebounced();
    },
    [svgContent, selectedCheckbox, trySVGDebounced]
  );

  const handleProductChange = (event) => {
    setSelectedProduct(event.target.value);
    setSelectedSide("");
    setDimensions({ width: "", height: "" });
  };

  const handleSideChange = (event) => {
    const selSide = event.target.value;
    setSelectedSide(selSide);
    const product = products.find((p) => p.name === selectedProduct);
    const engraveSide = product?.engraveSides.find((s) => s.side === selSide);
    setDimensions({
      width: engraveSide?.width || "",
      height: engraveSide?.height || "",
    });
  };

  return (
    <div className="outer-container">
      <div className="container">
        {/* SOL LAYOUT PANEL */}
        <div className="layout-panel">
          <PathContext.Provider value={[]}>
            <div>
              {shopDataRef.current.map((shop) => (
                <RecursiveAccordion
                  key={shop.name}
                  data={shop}
                  onPick={onPick}
                />
              ))}
            </div>
          </PathContext.Provider>
        </div>

        {/* SOL FORM PANEL */}
        <div className="left-panel">
          {hoverImgVisible === true ? (
            <img
              style={{ width: "100px", height: "100px", objectFit: "cover" }}
              src={hoverImgUrl}
              alt=""
            />
          ) : null}

          <h2>Düzenleme Paneli</h2>
          <h3 style={{ color: "#705C53" }}> {presentPath}</h3>

          <FormControl
            fullWidth
            style={{
              marginBottom: "20px",
              marginTop: "20px",
              width: "50%",
              marginLeft: "1%",
            }}
          >
            <InputLabel style={{ width: "63%", marginLeft: "15%" }}>
              Product
            </InputLabel>
            <Select
              style={{ width: "63%", marginLeft: "15%" }}
              label="Product"
              value={selectedProduct}
              onChange={handleProductChange}
            >
              {products.map((product) => (
                <MenuItem key={product.name} value={product.name}>
                  {product.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedProduct && (
            <FormControl
              fullWidth
              style={{ marginBottom: "20px", width: "50%", marginLeft: "1%" }}
            >
              <InputLabel style={{ width: "63%", marginLeft: "15%" }}>
                Engrave Side
              </InputLabel>
              <Select
                label="Engrave Side"
                value={selectedSide}
                onChange={handleSideChange}
                style={{ width: "63%", marginLeft: "15%" }}
              >
                {products
                  .find((product) => product.name === selectedProduct)
                  ?.engraveSides.map((side) => (
                    <MenuItem key={side.side} value={side.side}>
                      {side.side}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          {visibility.textAreaVisible && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <TextField
                onChange={handleChange}
                value={lineHeight}
                style={{
                  marginBottom: "20px",
                  width: "63%",
                  marginLeft: "50px",
                }}
                id="outlined-number"
                label="Satır Aralığı"
                type="number"
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </div>
          )}

         {visibility.text1Visible && (
            <div>
              <TextField
                id="textOne"
                label="Metin Girin"
                variant="standard"
                defaultValue={textInputs.text}   
                onBlur={commitOnBlur("textOne")}
                InputLabelProps={{ shrink: true }}    
                style={{ marginBottom: "20px", width: "80%" }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedCheckbox === "textOne"}
                    onChange={handleCheckboxChange}
                    name="textOne"
                  />
                }
              />
            </div>
          )}

          {visibility.text2Visible && (
            <div>
              <TextField
                id="textTwo"
                label="Metin Girin"
                variant="standard"
                defaultValue={textInputs.text1}
                onBlur={commitOnBlur("textTwo")}
                style={{ marginBottom: "20px", width: "80%" }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedCheckbox === "textTwo"}
                    onChange={handleCheckboxChange}
                    name="textTwo"
                  />
                }
              />
            </div>
          )}

          {visibility.text3Visible && (
            <div>
              <TextField
                id="textThree"
                label="Metin Girin"
                variant="standard"
                defaultValue={textInputs.text2}   
                onBlur={commitOnBlur("textThree")}
                InputLabelProps={{ shrink: true }}    
                style={{ marginBottom: "20px", width: "80%" }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedCheckbox === "textThree"}
                    onChange={handleCheckboxChange}
                    name="textThree"
                  />
                }
              />
            </div>
          )}

          {visibility.text4Visible && (
            <div>
              <TextField
                id="textFour"
                label="Metin Girin"
                variant="standard"
                defaultValue={textInputs.text3}   
                onBlur={commitOnBlur("textFour")}
                InputLabelProps={{ shrink: true }}    
                style={{ marginBottom: "20px", width: "80%" }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedCheckbox === "textFour"}
                    onChange={handleCheckboxChange}
                    name="textFour"
                  />
                }
              />
            </div>
          )}

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            {visibility.textAreaVisible && (
              <>
                <div>
                  <TextField
                    id="textArea"
                    label="Metin Girin"
                    variant="standard"
                    defaultValue={textInputs.textArea}
                    onBlur={commitOnBlur("textArea")}
                     InputLabelProps={{ shrink: true }}    
                    multiline
                    rows={4}
                    InputProps={{
                      style: { marginBottom: "10px", width: "60%" },
                    }}
                    style={{ marginBottom: "10px", width: "300%" }}
                  />
                </div>
                <div>
                  {lineCounts.map((count, index) => (
                    <p key={index}>
                      Satır {index + 1}: {count} karakter
                    </p>
                  ))}
                </div>
                <div style={{ marginBottom: "20px" }}>
                  <ButtonGroup variant="outlined" aria-label="text alignment">
                    <Button onClick={() => handleAlignmentChange("left")}>
                      Sola Yatır
                    </Button>
                    <Button onClick={() => handleAlignmentChange("center")}>
                      Ortala
                    </Button>
                    <Button onClick={() => handleAlignmentChange("right")}>
                      Sağa Yatır
                    </Button>
                  </ButtonGroup>
                </div>
              </>
            )}
          </div>

          {!visibility.textAreaVisible && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              style={{ marginBottom: "20px" }}
            >
              <Button
                id={"up"}
                onClick={handleButtonClick}
                variant="outlined"
                startIcon={<ArrowUpwardIcon />}
                style={{ marginBottom: "10px" }}
              />
              <Box display="flex">
                <Button
                  id={"left"}
                  onClick={handleButtonClick}
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  style={{ marginRight: "10px" }}
                />
                <Button
                  id={"right"}
                  onClick={handleButtonClick}
                  variant="outlined"
                  startIcon={<ArrowForwardIcon />}
                  style={{ marginLeft: "10px" }}
                />
              </Box>
              <Button
                id={"down"}
                onClick={handleButtonClick}
                variant="outlined"
                startIcon={<ArrowDownwardIcon />}
                style={{ marginTop: "10px" }}
              />
            </Box>
          )}

          <div>
            <CustomButton
              variant="outlined"
              onClick={handleClickOpen}
              style={{ marginRight: "10px" }}
            >
              SVG İndir
            </CustomButton>
            <CustomButton variant="outlined" onClick={trySVGRaw}>
              Resmi Dene
            </CustomButton>
          </div>
        </div>

        {/* SAĞ ÖNİZLEME PANELİ - Memoized Component */}
        <div className="right-panel">
          <SvgPreview svgContent={svgContent} svgExample={svgExample} />
        </div>
      </div>

      {/* İndir Dialog */}
      <div>
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Download SVG</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Please enter a name for the file.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="File Name"
              type="text"
              fullWidth
              variant="outlined"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={downloadSVG} variant="contained" color="primary">
              Download
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default SvgConverter;
